/* ==========================================================================
   SPLASH Weekly Draw — the page's behaviour.
   Reads everything from SplashData (weekly-data.js) and draws it. It never
   talks to the chain itself. No inline styles or scripts: the only styling
   it does is setting a few custom properties through the CSSOM.
   ========================================================================== */
(function () {
  'use strict';

  var MAX_DOTS = 520;
  var MAX_TICKETS = 50;
  var STILL_LOADING_MS = 8000;

  var body = document.body;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function $(sel) { return document.querySelector(sel); }
  function $$(sel) { return Array.prototype.slice.call(document.querySelectorAll(sel)); }
  function setText(sel, text) { $$(sel).forEach(function (n) { n.textContent = text; }); }
  function make(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text !== undefined) n.textContent = text;
    return n;
  }

  var el = {
    pool: $('[data-pool]'),
    coping: $('[data-coping]'),
    countdown: $('[data-countdown]'),
    countdownLabel: $('[data-countdown-label]'),
    rehearsal: $('[data-rehearsal]'),
    wallet: $('[data-wallet]'),
    stalled: $('[data-stalled]'),
    guest: $('[data-guest]'),
    marks: $$('[data-marks]'),
    empty: $('[data-empty-copy]'),
    droplet: $('[data-droplet]'),
    winner: $('[data-winner]'),
    statusText: $('[data-status-text]'),
    retry: $('[data-retry]'),
    coin: $('[data-drop-coin]'),
    ring: $('[data-ring]'),
    less: $('[data-less]'),
    more: $('[data-more]'),
    buy: $('[data-buy]'),
    after: $('[data-after]'),
    dots: $('[data-dots]'),
    dotScale: $('[data-dot-scale]'),
    history: $('[data-history]'),
    realmLine: $('[data-realm-line]'),
    foot: $('[data-foot]'),
    toast: $('[data-toast]'),
    announce: $('[data-announce]')
  };

  var state = null;
  var prev = null;
  var qty = 1;
  var closeAt = 0;
  var buying = false;
  var loadingTimer = 0;
  var toastTimer = 0;

  /* ---- Formatting: honest units, nothing dressed up ---------------------- */
  function gnot(n) { return Number(n).toLocaleString('en-IE', { minimumFractionDigits: 1, maximumFractionDigits: 2 }); }
  function whole(n) { return Math.round(n).toLocaleString('en-IE'); }
  function pct(part, total) {
    if (!total) return '0%';
    var p = part / total * 100;
    if (p >= 99.95) return '100%';
    if (p === 0) return '0%';
    // Round down, never up: we never overstate anyone's chance.
    return (p >= 10 ? (Math.floor(p * 10) / 10).toFixed(1) : (Math.floor(p * 100) / 100).toFixed(2)) + '%';
  }
  function short(addr) {
    if (addr === 'you') return 'You';
    return addr && addr.length > 14 ? addr.slice(0, 6) + '…' + addr.slice(-4) : addr;
  }
  function potTotal(s) { return Math.round((s.pot.tickets + s.pot.flips) * 100) / 100; }
  function isEmptyWeek(s) { return s.phase === 'open' && potTotal(s) === 0 && s.entries.total === 0; }

  function countdownText(secs) {
    secs = Math.max(0, Math.floor(secs));
    var d = Math.floor(secs / 86400);
    var h = Math.floor(secs % 86400 / 3600);
    var m = Math.floor(secs % 3600 / 60);
    var s = secs % 60;
    if (d > 0) return d + 'd ' + h + 'h ' + m + 'm';
    if (h > 0) return h + 'h ' + m + 'm';
    if (m > 0) return m + 'm ' + String(s).padStart(2, '0') + 's';
    return secs > 0 ? s + 's' : 'Closing…';
  }

  /* ---- Small helpers for feedback ----------------------------------------- */
  function toast(message) {
    el.toast.textContent = message;
    el.toast.classList.add('is-shown');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () { el.toast.classList.remove('is-shown'); }, 3600);
  }
  function announce(message) { el.announce.textContent = message; }

  function replay(node, cls) {
    node.classList.remove(cls);
    void node.offsetWidth;                              // restart the animation
    node.classList.add(cls);
  }

  /* ---- The pool --------------------------------------------------------------- */
  function poolHeight() { return el.pool.getBoundingClientRect().height || 330; }

  function renderPool(s, opts) {
    var total = potTotal(s);
    var past = s.history.slice(0, 4).map(function (w) { return w.amount; });
    var scale = 1.35 * Math.max.apply(null, past.concat([total, 1]));     // the record week fills to ~3/4
    var empty = isEmptyWeek(s);
    var drained = s.phase === 'drawn';
    var level = empty || drained ? 0.04 : Math.min(0.74, Math.max(0.08, total / scale));
    var share = total > 0 ? s.pot.flips / total : 0;

    el.pool.classList.toggle('is-draining', drained);
    el.pool.style.setProperty('--level', String(level));
    el.pool.style.setProperty('--coin-share', String(share));

    var h = poolHeight();
    var waterH = h * level;
    el.pool.classList.toggle('is-shallow', waterH < 150);
    el.pool.classList.toggle('is-thin-flips', waterH * share < 32);

    setText('[data-prize]', gnot(total));
    setText('[data-from-tickets]', gnot(s.pot.tickets) + ' GNOT');
    setText('[data-from-flips]', gnot(s.pot.flips) + ' GNOT');

    // Past weeks as high-water marks; drop a label if it would sit on top of another
    var lastY = -100;
    var waterY = h * (1 - level);
    var marks = s.history.slice(0, 3).map(function (w) {
      return { week: w.week, amount: w.amount, frac: Math.max(0.04, 1 - w.amount / scale) };
    }).sort(function (a, b) { return a.frac - b.frac; });
    el.marks.forEach(function (box) { box.replaceChildren(); });
    marks.forEach(function (m) {
      var y = m.frac * h;
      var showLabel = y - lastY >= 26;
      if (showLabel) lastY = y;
      el.marks.forEach(function (box) {
        var line = make('div', 'mark');
        line.style.setProperty('--mark', String(m.frac));
        if (showLabel) {
          // keep the label clear of the waterline: if it would straddle it, hang it under the line
          var straddles = y - 26 < waterY + 8 && y > waterY - 8;
          line.appendChild(make('span', 'mark__text' + (straddles ? ' mark__text--below' : ''), 'WK' + m.week + '\n' + gnot(m.amount)));
        }
        box.appendChild(line);
      });
    });

    el.empty.hidden = !empty;
    el.droplet.hidden = !empty;

    if (drained && s.winner) {
      el.winner.textContent = s.winner.you
        ? 'You took it — ' + gnot(s.winner.amount) + ' GNOT'
        : short(s.winner.address) + ' took ' + gnot(s.winner.amount) + ' GNOT';
      el.winner.hidden = false;
    } else {
      el.winner.hidden = true;
    }

    el.pool.setAttribute('aria-label', empty
      ? "This week's pot is empty so far. Nobody's in yet."
      : "This week's pot: " + gnot(total) + ' GNOT — ' + gnot(s.pot.tickets) + ' from tickets, ' + gnot(s.pot.flips) + ' from coin flips.');

    if (opts && opts.dropCoin && !reduceMotion.matches) {
      replay(el.coin, 'is-dropping');
      window.setTimeout(function () { replay(el.ring, 'is-rippling'); }, 640);
    }
  }

  /* ---- Your chance: one dot per entry (or per handful, in a big week) ------- */
  function renderDots(s, grew) {
    var total = s.entries.total;
    var mine = s.entries.mine || 0;
    var per = Math.max(1, Math.ceil(total / MAX_DOTS));
    var n = Math.ceil(total / per);
    var mineDots = mine > 0 ? Math.max(1, Math.round(mine / per)) : 0;
    var mineAt = {};
    var step = mineDots ? n / mineDots : 0;
    for (var k = 0; k < mineDots; k++) mineAt[Math.min(n - 1, Math.floor(k * step + step / 2))] = true;

    var before = el.dots.children.length;
    var frag = document.createDocumentFragment();
    for (var i = 0; i < n; i++) {
      var cls = 'dot';
      if (mineAt[i]) cls += ' is-mine';
      if (grew && i >= before && !reduceMotion.matches) cls += ' is-new';
      frag.appendChild(make('span', cls));
    }
    el.dots.replaceChildren(frag);
    el.dots.setAttribute('aria-label', whole(total) + ' entries in the pool' + (mine ? ', ' + whole(mine) + ' of them yours' : ''));
    el.dotScale.hidden = per === 1;
    el.dotScale.textContent = 'each dot is ' + per + ' entries';
  }

  /* ---- You, getting in, your chance --------------------------------------------- */
  function renderYou(s) {
    var mine = s.entries.mine;
    var total = s.entries.total;
    if (mine === null) {
      setText('[data-you-text]', 'Connect a wallet to see your entries');
      setText('[data-you-odds]', '');
      setText('[data-chance-pct]', '—');
      setText('[data-chance-text]', 'Your share shows up here once a wallet is connected.');
    } else if (mine === 0) {
      setText('[data-you-text]', 'Not in this week yet');
      setText('[data-you-odds]', whole(total) + ' entries in play');
      setText('[data-chance-pct]', '0%');
      setText('[data-chance-text]', total ? 'None of the ' + whole(total) + ' entries are yours yet.' : 'Nobody has an entry yet — including you.');
    } else {
      setText('[data-you-text]', whole(mine) + ' of ' + whole(total) + ' entries');
      setText('[data-you-odds]', pct(mine, total) + ' chance ↓');
      setText('[data-chance-pct]', pct(mine, total));
      setText('[data-chance-text]', whole(mine) + ' of the ' + whole(total) + ' entries are yours.');
    }
    $('[data-you-odds]').classList.toggle('you__odds--quiet', mine === 0);
  }

  function renderGetIn(s) {
    var t = s.ticket;
    var cost = qty * t.price;
    var add = qty * t.entries;
    var mine = s.entries.mine || 0;
    var afterMine = mine + add;
    var afterTotal = s.entries.total + add;
    var empty = isEmptyWeek(s);

    setText('[data-qty]', String(qty));
    setText('[data-qty-word]', qty === 1 ? 'ticket' : 'tickets');
    setText('[data-cost]', whole(cost));
    setText('[data-add-entries]', whole(add));
    el.less.disabled = qty <= 1;
    el.more.disabled = qty >= MAX_TICKETS;

    var open = s.phase === 'open';
    el.buy.disabled = !open || buying;
    el.buy.textContent = buying ? 'Waiting for the block…'
      : !open ? 'This week is closed'
      : (empty ? 'Make the first splash — ' : 'Jump in — ') + whole(cost) + ' GNOT';

    var lead = (empty && mine === 0)
      ? "You'd hold " + whole(add) + ' of ' + whole(add) + ' entries — 100%, until someone else joins.'
      : "With this you'd hold " + whole(afterMine) + ' of ' + whole(afterTotal) + ' entries — a ' + pct(afterMine, afterTotal) + ' chance.';
    var house = Math.round(t.houseShare * 100) + '% of each ticket is held for the house and only kept if the draw runs.';
    var tail = s.rehearsal ? ' Pretend GNOT — this is a rehearsal.' : " You'll connect a wallet at this step, not before.";
    el.after.replaceChildren(make('strong', '', lead), document.createTextNode(' ' + house + tail));
  }

  /* ---- Proof and history ------------------------------------------------------- */
  function realmLink(path) {
    if (!state.explorer) return make('span', '', path);
    var a = make('a', '', path + ' ↗');
    a.href = state.explorer.replace(/\/$/, '') + '/' + path.replace(/^gno\.land\//, '');
    a.rel = 'noopener';
    return a;
  }

  function renderProof(s) {
    var away = Math.max(0, s.closeBlock - s.currentBlock);
    setText('[data-fair-block]', whole(s.closeBlock));
    setText('[data-fair-blocks-away]', whole(away));
    setText('[data-fair-spb]', String(s.secondsPerBlock));
    setText('[data-fair-round]', whole(s.randomnessRound));
    setText('[data-fair-split]', gnot(s.pot.tickets) + ' + ' + gnot(s.pot.flips));
    setText('[data-fair-house]', Math.round(s.ticket.houseShare * 100) + '%');
    setText('[data-fair-house-amount]', gnot(s.ticket.price * s.ticket.houseShare));
    setText('[data-fair-price]', whole(s.ticket.price));
    el.realmLine.replaceChildren(realmLink(s.realm));
  }

  function renderHistory(s) {
    var top = Math.max.apply(null, s.history.map(function (w) { return w.amount; }).concat([1])) * 1.2;
    var frag = document.createDocumentFragment();
    s.history.slice(0, 3).forEach(function (w) {
      var li = make('li', 'week');
      var head = make('div', 'week__head');
      head.appendChild(make('span', 'label', 'Week ' + w.week));
      head.appendChild(make('span', 'week__amount', gnot(w.amount) + ' GNOT'));
      var bar = make('div', 'bar');
      var fill = make('div', 'bar__fill');
      fill.style.setProperty('--fill', String(w.amount / top));
      bar.appendChild(fill);
      var who = make('div', 'week__who');
      who.appendChild(make('span', '', (w.winner === 'you' ? 'You' : short(w.winner)) + ' took it'));
      who.appendChild(make('span', '', 'drand ' + whole(w.round)));
      li.append(head, bar, who, make('p', 'week__meta', whole(w.entries) + ' entries · ' + gnot(w.fromFlips) + ' GNOT of it from coin flips'));
      frag.appendChild(li);
    });
    el.history.replaceChildren(frag);
  }

  function renderFoot(s) {
    var inner = make('div', 'foot__inner');
    // fair__realm ON BOTH PATH LINES -- see duel-view.js's renderFoot for the
    // reasoning. THE BEACON LINE NEEDS IT AS MUCH AS THE REALM LINE: this page
    // prints two package paths, not one, and both are 40 characters of unbroken
    // base32. Measured content widths here were 450 and 402 inside a 358px box.
    inner.append(
      make('span', 'fair__realm', 'Realm ' + s.realm),
      make('span', 'fair__realm', 'Randomness ' + s.beacon),
      make('span', '', 'Tickets in wrapped GNOT' + (s.rehearsal ? ' · rehearsal build' : ''))
    );
    el.foot.replaceChildren(inner);
  }

  /* ---- Top of the page -------------------------------------------------------- */
  function renderTop(s) {
    el.rehearsal.hidden = !s.rehearsal;
    setText('[data-rehearsal-text]', 'Pretend GNOT — nothing here is on a chain yet. ' + s.network + ' itself is at block ' + whole(s.networkHeight) + '.');
    var guest = s.rehearsal && s.entries.mine !== null;
    el.guest.hidden = !guest;
    el.wallet.hidden = guest;

    renderWallet(s);

    // THE UNSETTLED BANNER. Shown when a week that is over has not been settled --
    // read from the chain, so it is right whether our keeper is dead, broke or
    // was never running. It tells the player the money is safe and that ANYBODY
    // can finish it, because both are true and the second is the whole reason
    // the calls are permissionless.
    // Same rewrite as the hourly page, for the same reason and on the same
    // day -- see hourly-view.js. This banner was word-for-word identical, so
    // fixing only the page the owner happened to screenshot would have left
    // the defect live on the other one.
    if (s.stalled) {
      el.stalled.hidden = false;
      var head = 'Week ' + s.stalled.index + ' closed and the winner has not been paid yet. '
        + 'The ' + gnot(s.stalled.pot) + ' GNOT is still in the contract \u2014 nobody can touch it except whoever wins.';
      var tail;
      if (s.stalled.due === false) {
        tail = ' It is waiting on drand round ' + whole(s.stalled.round)
          + ', which has not happened yet. Nobody can finish it before then, including us. '
          + 'It settles by itself shortly after that number exists.';
      } else if (s.stalled.due === true) {
        tail = ' Round ' + whole(s.stalled.round) + ' has happened, so it can be finished now. '
          + 'Our keeper does this automatically within a minute or two \u2014 but it is an open call, '
          + 'so if we ever stopped, anyone could finish it and the winner would still be paid.';
      } else {
        tail = ' Finishing it is an open call \u2014 we do it automatically, and anyone else could.';
      }
      setText('[data-stalled-text]', head + tail);
    } else {
      el.stalled.hidden = true;
    }

    setText('[data-week]', 'Week ' + s.week);
    setText('[data-close-block]', whole(s.closeBlock));
    setText('[data-current-block]', whole(s.currentBlock));

    if (s.sponsor) {
      setText('[data-sponsor-name]', s.sponsor);
      el.coping.classList.toggle('is-long', s.sponsor.length > 24);
      el.coping.classList.remove('is-plain');
      el.coping.setAttribute('aria-label', 'Sponsored by ' + s.sponsor);
    } else {
      setText('[data-sponsor-name]', 'SPLASH · Week ' + s.week);
      el.coping.classList.remove('is-long');
      el.coping.classList.add('is-plain');
      el.coping.removeAttribute('aria-label');
    }

    closeAt = Date.now() + Math.max(0, s.closeBlock - s.currentBlock) * s.secondsPerBlock * 1000;
    tick();
  }

  function tick() {
    if (!state) return;
    if (state.phase === 'drawing') {
      setText('[data-countdown-label]', 'Draw closed');
      el.countdown.textContent = 'Picking the winner…';
    } else if (state.phase === 'drawn') {
      setText('[data-countdown-label]', 'Drawn');
      el.countdown.textContent = state.winner && state.winner.you ? 'You won!' : 'Winner picked';
    } else {
      setText('[data-countdown-label]', 'Draw closes in');
      el.countdown.textContent = countdownText((closeAt - Date.now()) / 1000);
    }
  }

  /* ---- Putting it together ----------------------------------------------------- */
  function render(next) {
    prev = state;
    state = next;
    window.clearTimeout(loadingTimer);

    var first = !prev;
    var potUp = prev && prev.week === next.week && potTotal(next) > potTotal(prev) && next.phase === 'open';
    var grew = prev && prev.week === next.week && next.entries.total > prev.entries.total;

    body.classList.remove('is-loading', 'is-offline');
    body.classList.toggle('is-empty', isEmptyWeek(next));
    body.classList.toggle('is-drawing', next.phase === 'drawing');

    if (first) el.pool.classList.add('no-anim');
    renderTop(next);
    renderPool(next, { dropCoin: potUp });
    renderYou(next);
    renderGetIn(next);
    renderDots(next, grew);
    renderProof(next);
    renderHistory(next);
    renderFoot(next);
    if (first) window.requestAnimationFrame(function () { window.requestAnimationFrame(function () { el.pool.classList.remove('no-anim'); }); });

    if (prev && prev.phase !== 'drawn' && next.phase === 'drawn' && next.winner) {
      announce(next.winner.you ? 'You won this week’s draw: ' + gnot(next.winner.amount) + ' GNOT.' : short(next.winner.address) + ' won this week’s draw.');
    }
    if (prev && prev.week !== next.week) {
      qty = 1;
      renderGetIn(next);
      announce('Week ' + next.week + ' is open.');
    }
  }

  function showError(message) {
    window.clearTimeout(loadingTimer);
    body.classList.remove('is-loading');
    if (state) { toast(message); return; }            // keep the last good numbers on screen
    body.classList.add('is-offline');
    el.droplet.hidden = false;
    el.statusText.textContent = message;
    el.retry.hidden = false;
  }

  function startLoading() {
    body.classList.add('is-loading');
    body.classList.remove('is-offline');
    el.droplet.hidden = false;
    el.retry.hidden = true;
    el.statusText.textContent = 'Reading the realm…';
    loadingTimer = window.setTimeout(function () {
      el.statusText.textContent = 'Still reading the realm — the chain may be slow.';
    }, STILL_LOADING_MS);
    window.SplashData.start();
  }

  /* ---- Controls ------------------------------------------------------------------ */
  el.less.addEventListener('click', function () { if (qty > 1) { qty -= 1; renderGetIn(state); } });
  el.more.addEventListener('click', function () { if (qty < MAX_TICKETS) { qty += 1; renderGetIn(state); } });

  el.buy.addEventListener('click', function () {
    if (!state || state.phase !== 'open' || buying) return;
    var count = qty;
    buying = true;
    renderGetIn(state);
    window.SplashData.buyTickets(count).then(function (res) {
      var msg = "You're in — " + whole(res.entries) + (state.rehearsal ? ' pretend' : '') + ' entries added.';
      toast(msg);
      announce(msg + ' Your chance is now ' + pct(state.entries.mine, state.entries.total) + '.');
      qty = 1;
    }).catch(function (err) {
      toast(err.message);
    }).then(function () {
      buying = false;
      renderGetIn(state);
    });
  });


  // THE WALLET PILL, one implementation written three times on purpose.
  //
  // It carries its OWN shortener rather than calling the file's. Earlier today
  // this exact block was copied between two views whose shorteners are named
  // short() and who() -- the second threw ReferenceError on every render,
  // silently, and the pill simply never updated. And the duel's who() answers
  // "You" for your own address, which is right in a list of players and wrong on
  // a pill whose whole job is to show WHICH address. Self-contained is cheaper
  // than correct-by-coincidence.
  function renderWallet(s) {
    var a = s && s.wallet && s.wallet.address;
    if (a) {
      el.wallet.textContent = a.slice(0, 6) + '\u2026' + a.slice(-4);
      el.wallet.title = a + ' \u2014 click to disconnect';
      el.wallet.classList.add('is-connected');
      el.wallet.disabled = false;
    } else {
      el.wallet.textContent = 'Connect wallet';
      el.wallet.removeAttribute('title');
      el.wallet.classList.remove('is-connected');
      el.wallet.disabled = false;
    }
  }

  el.wallet.addEventListener('click', function () {
    var D = window.SplashData;
    if (el.wallet.classList.contains('is-connected')) {
      D.disconnect().then(function (res) { toast(res.message); }).catch(function (err) { toast(err.message); });
      return;
    }
    D.connectWallet().then(function (res) { toast(res.message); }).catch(function (err) { toast(err.message); });
  });

  el.retry.addEventListener('click', startLoading);

  window.SplashData.onChange(function (evt) {
    if (evt.type === 'state') render(evt.state);
    else if (evt.type === 'error') showError(evt.message);
  });

  window.addEventListener('resize', function () { if (state) renderPool(state); });

  // Demo switches (#demo-empty, #demo-draw …) start the page over with that story
  window.addEventListener('hashchange', function () {
    if (!/^#demo-[a-z]+$/.test(window.location.hash)) return;
    state = null;
    prev = null;
    qty = 1;
    buying = false;
    body.classList.remove('is-empty', 'is-drawing');
    el.winner.hidden = true;
    el.pool.classList.remove('is-draining');
    el.dots.replaceChildren();
    window.scrollTo(0, 0);
    startLoading();
  });
  window.setInterval(tick, 1000);
  startLoading();
})();
